import { useNavigate, useSearch } from '@tanstack/react-router'
import { CheckCircle2, Clock, User } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import type { ColumnConfig } from '@/components/tables/enhanced-data-table'
import type {
  LeaveRequestDto,
  SupervisorApprovedRequestsParams,
  SupervisorPendingRequestsParams,
} from '@/utils/api/leave-service/models'

import { EnhancedDataTable } from '@/components/tables/enhanced-data-table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useSupervisorAccess } from '@/lib/supervisor-access'
import {
  useSupervisorApprovedRequests,
  useSupervisorPendingRequests,
} from '@/utils/api/leave-service'
import { formatDate } from '@/utils/DateHelpers'

import { LeaveDetailsDrawer } from '../components/LeaveDetailsDrawer'
import { TrackingDialog } from '../components/TrackingDialog'
import { AccessDeniedPage } from './AccessDeniedPage'

export function SupervisorApprovalsPage() {
  const { requestId: urlRequestId, openDialog: urlOpenDialog } = useSearch({ from: '/_authenticated/leave/approvals' })
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'pending' | 'history'>('pending')
  const [selectedRequestId, setSelectedRequestId] = useState<number | null>(null)
  const [selectedRequest, setSelectedRequest] = useState<LeaveRequestDto | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false)

  const [pendingPage, setPendingPage] = useState(0)
  const [historyPage, setHistoryPage] = useState(0)
  const pageSize = 10
  const supervisorAccessQuery = useSupervisorAccess()
  const hasSupervisorAccess = supervisorAccessQuery.data === true

  // Pending requests query
  const pendingParams: SupervisorPendingRequestsParams = {
    pageable: {
      page: pendingPage,
      size: pageSize,
      sort: ['submittedAt,desc'],
    },
  }

  const pendingQuery = useSupervisorPendingRequests(pendingParams, {
    query: { enabled: activeTab === 'pending' && hasSupervisorAccess },
  })

  // Approved/history requests query
  const historyParams: SupervisorApprovedRequestsParams = {
    pageable: {
      page: historyPage,
      size: pageSize,
      sort: ['lastModifiedDate,desc'],
    },
  }

  const historyQuery = useSupervisorApprovedRequests(historyParams, {
    query: { enabled: activeTab === 'history' && hasSupervisorAccess },
  })

  // Auto-open the leave details drawer when deep-linked from an approval email
  const deepLinkHandled = useRef(false)
  useEffect(() => {
    if (urlRequestId && urlOpenDialog && !deepLinkHandled.current) {
      deepLinkHandled.current = true
      // Use a microtask to avoid the "no setState in useEffect" lint rule
      queueMicrotask(() => {
        setSelectedRequestId(urlRequestId)
        setDrawerOpen(true)
        navigate({
          to: '/leave/approvals',
          search: {},
          replace: true,
        })
      })
    }
  }, [urlRequestId, urlOpenDialog, navigate])

  if (supervisorAccessQuery.isPending) {
    return (
      <div className="flex w-full justify-center">
        <Card className="w-full max-w-3xl">
          <CardHeader>
            <CardTitle>Checking access</CardTitle>
            <CardDescription>Please wait while we verify your permissions.</CardDescription>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            This usually takes a moment.
          </CardContent>
        </Card>
      </div>
    )
  }

  if (supervisorAccessQuery.data === false) {
    return <AccessDeniedPage />
  }

  // Column configuration for pending requests
  const pendingColumns: ColumnConfig<LeaveRequestDto>[] = [
    {
      key: 'userId',
      header: 'Applicant',
      cell: (_value, row) => (
        <div className="flex items-center gap-2">
          <User className="size-4 text-muted-foreground" />
          <span className="font-medium">{row.applicantName}</span>
        </div>
      ),
    },
    {
      key: 'leaveTypeName',
      header: 'Leave Type',
      cell: (_value, row) => <Badge>{row.leaveTypeName}</Badge>,
    },
    {
      key: 'startDate',
      header: 'Start Date',
      cell: (_value, row) =>
        row.startDate
          ? formatDate(new Date(row.startDate))
          : '—',
    },
    {
      key: 'endDate',
      header: 'End Date',
      cell: (_value, row) =>
        row.endDate ? formatDate(new Date(row.endDate)) : '—',
    },
    {
      key: 'workingDays',
      header: 'Days',
      cell: (_value, row) => `${row.workingDays} days`,
    },
    {
      key: 'balanceContext',
      header: 'Balance',
      sortable: false,
      filterable: false,
      cell: (_value, row) => {
        const balance = row.balanceContext?.currentBalance
        if (!balance) {
          return <span className="text-xs text-muted-foreground">No context</span>
        }

        return (
          <div className="text-xs space-y-1">
            <p>
              Taken:
              {' '}
              <span className="font-medium">{balance.daysTakenToDate ?? 0}</span>
            </p>
            <p>
              Remaining:
              {' '}
              <span className="font-medium">{balance.totalRemaining ?? 0}</span>
            </p>
            {(balance.carryoverRemaining ?? 0) > 0
              ? (
                <p>
                  Carryover:
                  {' '}
                  <span className="font-medium">{balance.carryoverRemaining}</span>
                </p>
              )
              : null}
          </div>
        )
      },
    },
    {
      key: 'submittedAt',
      header: 'Submitted',
      cell: (_value, row) =>
        row.updatedAt
          ? formatDate(new Date(row.updatedAt))
          : '—',
    },
    {
      key: 'actions',
      header: 'Actions',
      sortable: false,
      filterable: false,
      align: 'right',
      width: 220,
      cell: (_value, row) => (
        <div className="flex w-full items-center gap-2 justify-end">
          <Button
            variant="ghost"
            size="sm"
            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            onClick={() => {
              setSelectedRequestId(row.id!)
              setSelectedRequest(row)
              setDrawerOpen(true)
            }}
          >
            Process
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-slate-600 hover:text-slate-700 hover:bg-slate-50"
            onClick={() => {
              setSelectedRequestId(row.id!)
              setSelectedRequest(row)
              setTrackingDialogOpen(true)
            }}
          >
            Track
          </Button>
        </div>
      ),
    },
  ]

  // Column configuration for history
  const historyColumns: ColumnConfig<LeaveRequestDto>[] = [
    {
      key: 'userId',
      header: 'Applicant',
      cell: (_value, row) => (
        <div className="flex items-center gap-2">
          <User className="size-4 text-muted-foreground" />
          <span className="font-medium">{row.applicantName}</span>
        </div>
      ),
    },
    {
      key: 'leaveTypeName',
      header: 'Leave Type',
      cell: (_value, row) => <Badge>{row.leaveTypeName}</Badge>,
    },
    {
      key: 'startDate',
      header: 'Start Date',
      cell: (_value, row) =>
        row.startDate
          ? formatDate(new Date(row.startDate))
          : '—',
    },
    {
      key: 'endDate',
      header: 'End Date',
      cell: (_value, row) =>
        row.endDate ? formatDate(new Date(row.endDate)) : '—',
    },
    {
      key: 'workingDays',
      header: 'Days',
      cell: (_value, row) => `${row.workingDays} days`,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (_value, row) => <Badge variant="outline">{row.status?.replaceAll('_', ' ')}</Badge>,
    },
    {
      key: 'actions',
      header: 'Actions',
      sortable: false,
      filterable: false,
      align: 'right',
      width: 220,
      cell: (_value, row) => (
        <div className="flex w-full items-center gap-2 justify-end">
          <Button
            variant="ghost"
            size="sm"
            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            onClick={() => {
              setSelectedRequestId(row.id!)
              setSelectedRequest(row)
              setDrawerOpen(true)
            }}
          >
            View
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-slate-600 hover:text-slate-700 hover:bg-slate-50"
            onClick={() => {
              setSelectedRequestId(row.id!)
              setSelectedRequest(row)
              setTrackingDialogOpen(true)
            }}
          >
            Track
          </Button>
        </div>
      ),
    },
  ]

  const handleDrawerClose = () => {
    setDrawerOpen(false)
    setSelectedRequestId(null)
    setSelectedRequest(null)
  }

  const handleActionComplete = () => {
    // Refresh the current tab data
    if (activeTab === 'pending') {
      pendingQuery.refetch()
    }
  }

  return (
    <div className="space-y-6">

      <Card>
        <CardHeader>
          <CardTitle>Supervisor Approvals</CardTitle>
          <CardDescription>
            Manage leave requests that require your approval. Switch between pending approvals and
            your approval history.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={value => setActiveTab(value as any)}>
            <TabsList>
              <TabsTrigger value="pending">
                <Clock className="mr-2 size-4" />
                Pending Approvals
                {pendingQuery.data?.data?.totalElements !== undefined && (
                  <Badge variant="secondary" className="ml-2">
                    {pendingQuery.data.data.totalElements}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="history">
                <CheckCircle2 className="mr-2 size-4" />
                Approval History
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pending" className="mt-6">
              <EnhancedDataTable
                columns={pendingColumns}
                data={pendingQuery.data?.data?.content || []}
                loading={pendingQuery.isLoading}
                paginationCallbacks={{
                  onPageChange: setPendingPage,
                  totalCount: pendingQuery.data?.data?.totalElements || 0,
                  manualPagination: true,
                }}
                pageSize={pageSize}
              />
            </TabsContent>

            <TabsContent value="history" className="mt-6">
              <EnhancedDataTable
                columns={historyColumns}
                data={historyQuery.data?.data?.content || []}
                loading={historyQuery.isLoading}
                paginationCallbacks={{
                  onPageChange: setHistoryPage,
                  totalCount: historyQuery.data?.data?.totalElements || 0,
                  manualPagination: true,
                }}
                pageSize={pageSize}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <LeaveDetailsDrawer
        leaveRequestId={selectedRequestId}
        leaveRequest={selectedRequest}
        open={drawerOpen}
        onClose={handleDrawerClose}
        onActionComplete={handleActionComplete}
      />

      <TrackingDialog
        leaveRequestId={selectedRequestId}
        open={trackingDialogOpen}
        onOpenChange={setTrackingDialogOpen}
      />
    </div>
  )
}
